import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import api from '../api/client';
import { Depot, Vehicle, DeliveryPoint, RouteEntity } from '../types';
import { BR_ADDRESSES, AddressItem, fmtKm, fmtMin } from '../utils/mockState';
import { fetchRoadGeometry } from '../utils/osrm';
import { searchAddress } from '../utils/geocoding';
import './FleetOptimize.css';
import { TILE_URL, TILE_ATTRIBUTION, TILE_MAX_ZOOM } from '../utils/mapTiles';

declare const L: any;

const VEHICLE_COLORS = ['#0f4c5c', '#2a9d8f', '#e76f51', '#e9c46a', '#5c4770', '#3a4a53'];

interface VehicleResult {
  vehicleId: string;
  vehicleName: string;
  routeId: string;
  distanceM: number;
  durationS: number;
  stopsCount: number;
  capacityKg: number;
  usedWeightKg: number;
}

interface OptimizeResult {
  routes: RouteEntity[];
  vehicleResults: VehicleResult[];
  unassignedPoints: DeliveryPoint[];
}

export default function FleetOptimize() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [depots, setDepots] = useState<Depot[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [points, setPoints] = useState<DeliveryPoint[]>([]);

  const [selectedDepots, setSelectedDepots] = useState<string[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  const [selectedPointIds, setSelectedPointIds] = useState<string[]>([]);

  // Modal new CD
  const [cdModalOpen, setCdModalOpen] = useState(false);
  const [cdSaving, setCdSaving] = useState(false);
  const [cdName, setCdName] = useState('');
  const [cdAddress, setCdAddress] = useState('');
  const [cdCity, setCdCity] = useState('São Paulo');
  const [cdUf, setCdUf] = useState('SP');
  const [cdLat, setCdLat] = useState('-23.5615');
  const [cdLng, setCdLng] = useState('-46.6560');
  const [cdType, setCdType] = useState<'matriz' | 'distribuicao' | 'crossdock'>('distribuicao');

  // Address search (quick-add from known SP address list)
  const [searchAddr, setSearchAddr] = useState('');
  const [filteredAddrs, setFilteredAddrs] = useState<AddressItem[]>([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [activePointSuggestion, setActivePointSuggestion] = useState(-1);
  const [addingPoint, setAddingPoint] = useState(false);

  // CD Address search
  const [cdSuggestions, setCdSuggestions] = useState<AddressItem[]>([]);
  const [showCdSuggest, setShowCdSuggest] = useState(false);
  const [activeCdSuggestion, setActiveCdSuggestion] = useState(-1);
  const cdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCdAddressSearch = (q: string) => {
    setCdAddress(q);
    setActiveCdSuggestion(-1);
    if (!q.trim()) {
      setCdSuggestions([]);
      setShowCdSuggest(false);
      return;
    }
    if (cdTimer.current) clearTimeout(cdTimer.current);
    cdTimer.current = setTimeout(async () => {
      try {
        const remote = await searchAddress(q);
        setCdSuggestions(remote);
        setShowCdSuggest(true);
      } catch {
        // ignore
      }
    }, 400);
  };

  const selectCdAddress = (addr: AddressItem) => {
    setCdAddress(addr.label);
    setCdCity(addr.city);
    setCdUf(addr.uf);
    setCdLat(String(addr.lat));
    setCdLng(String(addr.lng));
    setCdSuggestions([]);
    setShowCdSuggest(false);
    setActiveCdSuggestion(-1);
  };

  // Result and map
  const [optimizing, setOptimizing] = useState(false);
  const [results, setResults] = useState<OptimizeResult | null>(null);
  const [activeRouteFilter, setActiveRouteFilter] = useState<number | null>(null);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  async function load() {
    setLoading(true);
    try {
      const [d, v, p] = await Promise.all([
        api.get<Depot[]>('/depots'),
        api.get<Vehicle[]>('/vehicles'),
        api.get<DeliveryPoint[]>('/points')
      ]);
      setDepots(d.data);
      setVehicles(v.data);
      setPoints(p.data);
      setSelectedDepots(d.data.map((x) => x.id));
      setSelectedVehicles(v.data.filter((x) => x.active).map((x) => x.id));
      setSelectedPointIds(p.data.map((x) => x.id));
    } catch {
      showToast('Erro ao carregar dados da frota.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Vehicles filtered by selected depots
  const visibleVehicles = useMemo(
    () => vehicles.filter((v) => selectedDepots.includes(v.homeDepotId)),
    [vehicles, selectedDepots]
  );

  // Capacity metrics
  const totalDemand = useMemo(
    () => points.filter((p) => selectedPointIds.includes(p.id)).reduce((sum, p) => sum + (p.weightKg || 0), 0),
    [points, selectedPointIds]
  );
  const totalCapacity = useMemo(
    () =>
      vehicles
        .filter((v) => selectedVehicles.includes(v.id) && selectedDepots.includes(v.homeDepotId))
        .reduce((sum, v) => sum + (v.capacityKg || 0), 0),
    [vehicles, selectedVehicles, selectedDepots]
  );
  const feasibilityStatus = totalCapacity >= totalDemand ? 'ok' : 'err';

  const toggleDepot = (dId: string) => {
    setSelectedDepots((prev) => {
      const isSelected = prev.includes(dId);
      if (isSelected) {
        const next = prev.filter((id) => id !== dId);
        // Desmarca veículos pertencentes ao CD desmarcado
        setSelectedVehicles((prevVehs) => {
          const remainingDepots = new Set(next);
          return prevVehs.filter((vId) => {
            const veh = vehicles.find((v) => v.id === vId);
            return veh ? remainingDepots.has(veh.homeDepotId) : false;
          });
        });
        return next;
      } else {
        const next = [...prev, dId];
        // Seleciona automaticamente os veículos ativos do CD marcado
        const newVehs = vehicles.filter((v) => v.homeDepotId === dId && v.active).map((v) => v.id);
        setSelectedVehicles((prevVehs) => Array.from(new Set([...prevVehs, ...newVehs])));
        return next;
      }
    });
  };
  const toggleVehicle = (vId: string) => {
    setSelectedVehicles((prev) => (prev.includes(vId) ? prev.filter((id) => id !== vId) : [...prev, vId]));
  };
  const togglePoint = (pId: string) => {
    setSelectedPointIds((prev) => (prev.includes(pId) ? prev.filter((id) => id !== pId) : [...prev, pId]));
  };

  const saveNewCD = async () => {
    if (!cdName.trim() || !cdAddress.trim()) {
      showToast('Preencha nome e endereço do CD.', 'error');
      return;
    }
    const latitude = parseFloat(cdLat);
    const longitude = parseFloat(cdLng);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      showToast('Latitude/longitude inválidas.', 'error');
      return;
    }
    setCdSaving(true);
    try {
      const { data } = await api.post<Depot>('/depots', {
        name: cdName.trim(),
        type: cdType,
        address: cdAddress.trim(),
        city: cdCity.trim(),
        uf: cdUf.trim().toUpperCase(),
        latitude,
        longitude
      });
      setDepots((prev) => [...prev, data]);
      setSelectedDepots((prev) => [...prev, data.id]);
      setCdModalOpen(false);
      setCdName('');
      setCdAddress('');
      showToast(`CD "${data.name}" cadastrado!`, 'success');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao cadastrar CD.', 'error');
    } finally {
      setCdSaving(false);
    }
  };

  // Address search (quick-add helper — sugere endereços conhecidos de SP com coordenadas reais)
  const pointTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleAddressSearch = (q: string) => {
    setSearchAddr(q);
    setActivePointSuggestion(-1);
    if (!q.trim()) {
      setFilteredAddrs([]);
      setShowSuggest(false);
      return;
    }
    const filtered = BR_ADDRESSES.filter((a) => a.label.toLowerCase().includes(q.toLowerCase()));
    setFilteredAddrs(filtered);
    setShowSuggest(true);

    if (pointTimer.current) clearTimeout(pointTimer.current);
    pointTimer.current = setTimeout(async () => {
      try {
        const remote = await searchAddress(q);
        const combined = [...filtered, ...remote.filter((r) => !filtered.find((l) => l.label === r.label))];
        setFilteredAddrs(combined);
      } catch {
        // Keep local results on network failure
      }
    }, 400);
  };

  const addPoint = async (addr: AddressItem) => {
    setAddingPoint(true);
    setShowSuggest(false);
    setSearchAddr('');
    setActivePointSuggestion(-1);
    try {
      const { data } = await api.post<DeliveryPoint>('/points', {
        title: addr.label,
        address: `${addr.label}, ${addr.city}/${addr.uf}`,
        latitude: addr.lat,
        longitude: addr.lng,
        weightKg: 250,
        priority: 'normal'
      });
      setPoints((prev) => [...prev, data]);
      setSelectedPointIds((prev) => [...prev, data.id]);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao adicionar parada.', 'error');
    } finally {
      setAddingPoint(false);
    }
  };

  const handleSuggestionKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    suggestions: AddressItem[],
    isOpen: boolean,
    setOpen: (isOpen: boolean) => void,
    activeIndex: number,
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>,
    select: (address: AddressItem) => void
  ) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!isOpen || suggestions.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && activeIndex >= 0 && activeIndex < suggestions.length) {
      event.preventDefault();
      select(suggestions[activeIndex]);
    }
  };

  const updatePointWeight = (point: DeliveryPoint, weightKg: number) => {
    const w = Math.max(1, weightKg);
    setPoints((prev) => prev.map((p) => (p.id === point.id ? { ...p, weightKg: w } : p)));
  };

  const persistPointWeight = async (point: DeliveryPoint) => {
    try {
      await api.patch(`/points/${point.id}`, { weightKg: point.weightKg });
    } catch {
      showToast('Erro ao salvar peso da parada.', 'error');
    }
  };

  const removePoint = async (point: DeliveryPoint) => {
    try {
      await api.delete(`/points/${point.id}`);
      setPoints((prev) => prev.filter((p) => p.id !== point.id));
      setSelectedPointIds((prev) => prev.filter((id) => id !== point.id));
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao remover parada.', 'error');
    }
  };

  const runVRPOptimization = async () => {
    if (selectedDepots.length === 0) {
      showToast('Selecione ao menos 1 depósito.', 'error');
      return;
    }
    const activeVehicleIds = selectedVehicles.filter((id) =>
      vehicles.some((v) => v.id === id && selectedDepots.includes(v.homeDepotId))
    );
    if (activeVehicleIds.length === 0) {
      showToast('Selecione pelo menos um veículo vinculado aos CDs participantes.', 'error');
      return;
    }
    if (selectedPointIds.length === 0) {
      showToast('Adicione paradas para otimizar.', 'error');
      return;
    }

    setOptimizing(true);
    try {
      const { data } = await api.post<OptimizeResult>('/fleet/optimize', {
        depotIds: selectedDepots,
        vehicleIds: activeVehicleIds,
        pointIds: selectedPointIds
      });
      setResults(data);
      showToast(`Otimização concluída: ${data.routes.length} rotas geradas e salvas!`, 'success');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao otimizar a frota.', 'error');
    } finally {
      setOptimizing(false);
    }
  };

  // Render Leaflet Fleet Map
  useEffect(() => {
    if (!results || !mapContainerRef.current) return;
    if (typeof L === 'undefined') return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const map = L.map(mapContainerRef.current).setView([-23.5615, -46.656], 11);
    L.tileLayer(TILE_URL, { maxZoom: TILE_MAX_ZOOM, attribution: TILE_ATTRIBUTION }).addTo(map);

    const bounds: any[] = [];
    let cancelled = false;

    results.routes.forEach((route, idx) => {
      const depot = depots.find((d) => d.id === route.depotId);
      if (!depot) return;
      const color = VEHICLE_COLORS[idx % VEHICLE_COLORS.length];
      const isDimmed = activeRouteFilter !== null && activeRouteFilter !== idx;

      const depotIcon = L.divIcon({
        className: 'custom-depot-pin',
        html: `<div style="background:#0b1b22;color:#fff;width:30px;height:30px;border-radius:8px;display:grid;place-items:center;font-size:12px;font-weight:700;box-shadow:0 4px 12px rgba(0,0,0,0.3);border:2px solid ${color};">CD</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });
      L.marker([depot.latitude, depot.longitude], { icon: depotIcon }).addTo(map).bindPopup(`<b>${depot.name}</b><br/>${depot.address}`);
      bounds.push([depot.latitude, depot.longitude]);

      const stops = route.stops || [];
      const routePoints = [
        { lat: depot.latitude, lng: depot.longitude },
        ...stops.map((s) => ({ lat: s.deliveryPoint.latitude, lng: s.deliveryPoint.longitude })),
        { lat: depot.latitude, lng: depot.longitude }
      ];
      const latlngs = routePoints.map((p) => [p.lat, p.lng]);
      latlngs.forEach((ll) => bounds.push(ll));

      const poly = L.polyline(latlngs, {
        color,
        weight: isDimmed ? 2 : 4,
        opacity: isDimmed ? 0.2 : 0.9,
        dashArray: isDimmed ? '4 4' : undefined
      }).addTo(map);

      fetchRoadGeometry(routePoints).then((geometry) => {
        if (cancelled || !geometry) return;
        poly.setLatLngs(geometry);
      });

      stops.forEach((s, sIdx) => {
        const pinIcon = L.divIcon({
          className: 'custom-stop-pin',
          html: `<div style="background:${color};color:#fff;width:24px;height:24px;border-radius:50%;display:grid;place-items:center;font-size:11px;font-family:'JetBrains Mono';font-weight:700;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.25);opacity:${isDimmed ? 0.3 : 1};">${sIdx + 1}</div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        });
        L.marker([s.deliveryPoint.latitude, s.deliveryPoint.longitude], { icon: pinIcon })
          .addTo(map)
          .bindPopup(`<b>Parada ${sIdx + 1}</b><br/>${s.deliveryPoint.address}<br/><b>Carga:</b> ${s.weightKg} kg`);
      });
    });

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [30, 30] });
    }

    mapRef.current = map;

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [results, activeRouteFilter, depots]);

  const resetResults = () => {
    setResults(null);
    setActiveRouteFilter(null);
    load();
  };

  if (loading) {
    return (
      <Layout>
        <div className="empty"><p>Carregando dados da frota...</p></div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* VRP Hero Banner */}
      <div
        className="card"
        style={{
          background: 'linear-gradient(135deg, #0f4c5c 0%, #082129 100%)',
          color: '#e9f2f4',
          padding: 28,
          marginBottom: 24,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="login-eyebrow" style={{ background: 'rgba(124, 211, 198, 0.14)', color: '#7cd3c6' }}>
            <span className="dot" style={{ background: '#7cd3c6' }}></span>
            Motor VRP com Restrição de Capacidade Real (OR-Tools)
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 8 }}>
            Otimização de Frota Multi-Depósito (VRP)
          </h2>
          <p style={{ color: '#8fa9af', maxWidth: 680, fontSize: 14, lineHeight: 1.5 }}>
            Distribua automaticamente paradas entre múltiplos veículos e depósitos (CDs), respeitando a capacidade
            máxima em kg de cada veículo. As rotas geradas aqui são salvas de verdade no banco de dados.
          </p>

          <div style={{ display: 'flex', gap: 24, marginTop: 18 }}>
            <div className="vrp-hero-metric">
              <div className="n mono" style={{ fontSize: 20, fontWeight: 600, color: '#fff' }}>Multi-CD</div>
              <div className="l" style={{ color: '#8fa9af', fontSize: 11, marginTop: 2 }}>Centros de Distribuição</div>
            </div>
            <div className="vrp-hero-metric">
              <div className="n mono" style={{ fontSize: 20, fontWeight: 600, color: '#fff' }}>Capacidade Real</div>
              <div className="l" style={{ color: '#8fa9af', fontSize: 11, marginTop: 2 }}>Bin-packing com kg estrito</div>
            </div>
            <div className="vrp-hero-metric">
              <div className="n mono" style={{ fontSize: 20, fontWeight: 600, color: '#fff' }}>OR-Tools</div>
              <div className="l" style={{ color: '#8fa9af', fontSize: 11, marginTop: 2 }}>Solver real de roteirização</div>
            </div>
          </div>
        </div>
      </div>

      {!results ? (
        <div className="form-grid">
          <div>
            {/* Step 1: Depots */}
            <div className="form-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <h3>
                  <span className="step-num">1</span>
                  Centros de Distribuição (CDs) Participantes
                </h3>
                <button className="btn btn-secondary btn-sm" onClick={() => setCdModalOpen(true)}>
                  + Novo CD
                </button>
              </div>
              <div className="step-desc">Selecione as bases de saída da sua frota</div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                {depots.map((d) => {
                  const isSelected = selectedDepots.includes(d.id);
                  return (
                    <div
                      key={d.id}
                      className={`vrp-vehicle-check ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleDepot(d.id)}
                    >
                      <div className="cbox">
                        {isSelected && (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </div>
                      <div className="veh-info">
                        <div className="name">{d.name}</div>
                        <div className="meta">{d.city}/{d.uf}</div>
                      </div>
                      <span className="badge b-teal" style={{ fontSize: 10, padding: '2px 6px' }}>
                        {d.type}
                      </span>
                    </div>
                  );
                })}
                {depots.length === 0 && <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Nenhum depósito cadastrado.</p>}
              </div>
            </div>

            {/* Step 2: Available Vehicles */}
            <div className="form-section">
              <h3>
                <span className="step-num">2</span>
                Veículos Disponíveis para Roteirização ({selectedVehicles.filter(id => visibleVehicles.some(v => v.id === id)).length} de {visibleVehicles.length})
              </h3>
              <div className="step-desc">Marque os veículos vinculados aos CDs participantes que farão as entregas</div>

              <div className="vrp-vehicle-list">
                {visibleVehicles.map((v) => {
                  const depot = depots.find((d) => d.id === v.homeDepotId);
                  const isSelected = selectedVehicles.includes(v.id);
                  return (
                    <div
                      key={v.id}
                      className={`vrp-vehicle-check ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleVehicle(v.id)}
                    >
                      <div className="cbox">
                        {isSelected && (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </div>
                      <div className="veh-info">
                        <div className="name">{v.name}</div>
                        <div className="meta">{v.plate} · {depot?.name.split(' ')[0] || 'CD'}</div>
                      </div>
                      <div className="cap-badge">{v.capacityKg} kg</div>
                    </div>
                  );
                })}
                {visibleVehicles.length === 0 && (
                  <p style={{ color: 'var(--text-3)', fontSize: 13, gridColumn: '1 / -1', padding: '8px 0' }}>
                    Nenhum veículo disponível para os CDs selecionados. Marque ao menos 1 CD com veículos cadastrados.
                  </p>
                )}
              </div>
            </div>

            {/* Step 3: Stops & Demand kg */}
            <div className="form-section">
              <h3>
                <span className="step-num">3</span>
                Paradas e Demandas de Carga ({selectedPointIds.length} de {points.length} selecionadas)
              </h3>
              <div className="step-desc">Endereços cadastrados como pontos de entrega da empresa</div>

              {/* Add stop search */}
              <div className="field" style={{ position: 'relative', marginBottom: 16 }}>
                <div className="input-wrap">
                  <svg className="icn" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ left: 12 }}>
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                  <input
                    type="text"
                    className="input"
                    style={{ paddingLeft: 38 }}
                    placeholder="Adicionar novo ponto de entrega (endereços SP)..."
                    value={searchAddr}
                    onChange={(e) => handleAddressSearch(e.target.value)}
                    onFocus={() => { if (searchAddr) setShowSuggest(true); }}
                    onKeyDown={(event) => handleSuggestionKeyDown(event, filteredAddrs, showSuggest, setShowSuggest, activePointSuggestion, setActivePointSuggestion, addPoint)}
                    disabled={addingPoint}
                    role="combobox"
                    aria-expanded={showSuggest}
                    aria-controls="fleet-point-suggestions"
                    aria-activedescendant={activePointSuggestion >= 0 ? `fleet-point-option-${activePointSuggestion}` : undefined}
                  />
                </div>

                {showSuggest && filteredAddrs.length > 0 && (
                  <div id="fleet-point-suggestions" className="addr-suggest" role="listbox">
                    {filteredAddrs.map((a, idx) => (
                      <div id={`fleet-point-option-${idx}`} key={idx} className={`addr-suggest-item ${activePointSuggestion === idx ? 'keyboard-active' : ''}`} role="option" aria-selected={activePointSuggestion === idx} onClick={() => addPoint(a)}>
                        <div className="pin">📍</div>
                        <div>
                          <div className="a">{a.label}</div>
                          <div className="c">{a.city}/{a.uf}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Stops list */}
              <div>
                {points.map((p) => (
                  <div key={p.id} className="vrp-stop-item">
                    <div className="cbox" style={{ marginRight: 4 }} onClick={() => togglePoint(p.id)}>
                      <input type="checkbox" checked={selectedPointIds.includes(p.id)} readOnly style={{ pointerEvents: 'none' }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13.5 }}>{p.title}</div>
                      <div style={{ color: 'var(--text-3)', fontSize: 11 }}>{p.address}</div>
                    </div>
                    <div className="vrp-weight-input">
                      <input
                        type="number"
                        value={p.weightKg}
                        onChange={(e) => updatePointWeight(p, parseInt(e.target.value, 10) || 0)}
                        onBlur={() => persistPointWeight(p)}
                        min={1}
                        step={10}
                      />
                      <span className="u">kg</span>
                    </div>
                    <button
                      type="button"
                      className="stop-btn"
                      onClick={() => removePoint(p)}
                      title="Remover ponto"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {points.length === 0 && (
                  <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Nenhuma parada cadastrada ainda. Use a busca acima.</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Summary Card */}
          <div>
            <div className="route-summary">
              <div className="summary-card">
                <h4>Viabilidade Operacional VRP</h4>

                <div className="summary-metric">
                  <span className="k">Demanda total de carga</span>
                  <span className="v mono">{totalDemand} <span className="u">kg</span></span>
                </div>

                <div className="summary-metric">
                  <span className="k">Capacidade da frota ativa</span>
                  <span className="v mono">{totalCapacity} <span className="u">kg</span></span>
                </div>

                <div className="summary-metric">
                  <span className="k">Veículos selecionados</span>
                  <span className="v mono">{selectedVehicles.length} <span className="u">veículos</span></span>
                </div>

                <div className={`vrp-feasibility ${feasibilityStatus}`}>
                  <div className="icn">{feasibilityStatus === 'ok' ? '✓' : '!'}</div>
                  <div>
                    <div className="k">
                      {feasibilityStatus === 'ok' ? 'Capacidade suficiente' : 'Capacidade insuficiente'}
                    </div>
                    <div className="d">
                      {feasibilityStatus === 'ok'
                        ? 'A frota selecionada comporta a carga demandada.'
                        : 'A demanda excede a capacidade dos veículos marcados — pode gerar paradas não atribuídas.'}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 20 }}>
                  <button
                    className="btn btn-primary btn-block btn-lg"
                    onClick={runVRPOptimization}
                    disabled={optimizing || selectedPointIds.length === 0 || selectedVehicles.length === 0}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                    </svg>
                    {optimizing ? 'Executando VRP...' : 'Executar Otimização VRP'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Results View */
        <div className="vrp-result">
          <div className="vrp-result-hero">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
                  Resultado da Roteirização de Frota
                </h3>
                <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 2 }}>
                  Rotas geradas pelo motor OR-Tools e já salvas no sistema
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary" onClick={resetResults}>
                  Nova otimização
                </button>
                <button className="btn btn-primary" onClick={() => navigate('/rotas')}>
                  Ver {results.routes.length} rotas geradas
                </button>
              </div>
            </div>

            <div className="vrp-result-metrics">
              <div className="vrp-result-metric">
                <div className="k">Rotas geradas</div>
                <div className="v mono">{results.routes.length} <span className="u">rotas</span></div>
              </div>
              <div className="vrp-result-metric">
                <div className="k">Paradas alocadas</div>
                <div className="v mono">{results.vehicleResults.reduce((s, r) => s + r.stopsCount, 0)} <span className="u">pontos</span></div>
                <div className="d">{results.unassignedPoints.length === 0 ? 'Sem paradas órfãs' : `${results.unassignedPoints.length} pendentes`}</div>
              </div>
              <div className="vrp-result-metric">
                <div className="k">Distância total</div>
                <div className="v mono">{fmtKm(results.vehicleResults.reduce((s, r) => s + r.distanceM, 0) / 1000)} <span className="u">km</span></div>
              </div>
              <div className="vrp-result-metric">
                <div className="k">Carga transportada</div>
                <div className="v mono">{results.vehicleResults.reduce((s, r) => s + r.usedWeightKg, 0)} <span className="u">kg</span></div>
              </div>
              <div className="vrp-result-metric">
                <div className="k">Utilização média</div>
                <div className="v mono">
                  {results.vehicleResults.length > 0
                    ? Math.round(results.vehicleResults.reduce((s, r) => s + (r.usedWeightKg / r.capacityKg) * 100, 0) / results.vehicleResults.length)
                    : 0}%
                </div>
              </div>
            </div>
          </div>

          {results.unassignedPoints.length > 0 && (
            <div className="card" style={{ padding: 16, marginBottom: 16, borderColor: 'var(--amber)' }}>
              <strong style={{ color: 'var(--amber-700)' }}>{results.unassignedPoints.length} parada(s) não couberam na frota selecionada:</strong>
              <ul style={{ marginTop: 6, paddingLeft: 18, fontSize: 13, color: 'var(--text-3)' }}>
                {results.unassignedPoints.map((p) => <li key={p.id}>{p.title} — {p.weightKg} kg</li>)}
              </ul>
            </div>
          )}

          {/* Leaflet Multi-vehicle Map */}
          <div className="vrp-fleet-map-card">
            <div className="vrp-fleet-map">
              <div ref={mapContainerRef} id="fleet-map"></div>

              <div className="vrp-fleet-legend">
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 4 }}>
                  Legenda da Frota:
                </div>
                {results.vehicleResults.map((r, idx) => {
                  const color = VEHICLE_COLORS[idx % VEHICLE_COLORS.length];
                  const isDimmed = activeRouteFilter !== null && activeRouteFilter !== idx;
                  return (
                    <div
                      key={r.vehicleId}
                      className={`vrp-legend-item ${isDimmed ? 'dim' : ''}`}
                      onClick={() => setActiveRouteFilter(activeRouteFilter === idx ? null : idx)}
                    >
                      <span className="swatch" style={{ background: color }}></span>
                      <div className="lbl">
                        {r.vehicleName}
                        <div className="sub">{r.stopsCount} paradas · {fmtKm(r.distanceM / 1000)} km</div>
                      </div>
                      <span className="count">{Math.round((r.usedWeightKg / r.capacityKg) * 100)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Vehicle Route Breakdown Cards */}
          <div className="vrp-vehicles-result">
            {results.vehicleResults.map((r, idx) => {
              const color = VEHICLE_COLORS[idx % VEHICLE_COLORS.length];
              const route = results.routes.find((rt) => rt.id === r.routeId);
              const vehicle = vehicles.find((v) => v.id === r.vehicleId);
              const depot = depots.find((d) => d.id === route?.depotId);
              const utilization = Math.round((r.usedWeightKg / r.capacityKg) * 100);
              return (
                <div key={r.vehicleId} className="vrp-vres-card" style={{ borderLeft: `4px solid ${color}` }}>
                  <div className="vrp-vres-head">
                    <div>
                      <div className="name">{r.vehicleName}</div>
                      <div className="plate">{vehicle?.plate} · {depot?.name}</div>
                    </div>
                    <span className="badge b-teal">{vehicle?.driverName || 'Motorista não atribuído'}</span>
                  </div>

                  <div className="vrp-vres-stats">
                    <div>
                      <div className="k">Distância</div>
                      <div className="v">{fmtKm(r.distanceM / 1000)} <span className="u">km</span></div>
                    </div>
                    <div>
                      <div className="k">Duração</div>
                      <div className="v">{fmtMin(r.durationS / 60)}</div>
                    </div>
                    <div>
                      <div className="k">Paradas</div>
                      <div className="v">{r.stopsCount} <span className="u">locais</span></div>
                    </div>
                    <div>
                      <div className="k">Carga</div>
                      <div className="v">{r.usedWeightKg} / {r.capacityKg} <span className="u">kg</span></div>
                    </div>
                  </div>

                  <div className="vrp-vres-util">
                    <div className="vrp-vres-util-head">
                      <span className="k">Taxa de ocupação</span>
                      <span className="v">{utilization}%</span>
                    </div>
                    <div className="vrp-util-bar">
                      <div className="vrp-util-fill" style={{ width: `${Math.min(100, utilization)}%`, background: color }}></div>
                    </div>
                  </div>

                  <div className="vrp-vres-stop-list">
                    {(route?.stops || []).map((s, sIdx) => (
                      <div key={s.id} className="vrp-vres-stop">
                        <div className="n" style={{ background: color }}>{sIdx + 1}</div>
                        <div className="a">
                          {s.deliveryPoint.title}
                          <span className="w">({s.weightKg} kg)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* New Depot Modal */}
      <Modal
        isOpen={cdModalOpen}
        onClose={() => setCdModalOpen(false)}
        title="Cadastrar Novo Centro de Distribuição (CD)"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setCdModalOpen(false)}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={saveNewCD} disabled={cdSaving}>
              {cdSaving ? 'Salvando...' : 'Salvar CD'}
            </button>
          </>
        }
      >
        <div>
          <div className="field">
            <label>Nome do Centro de Distribuição</label>
            <input
              type="text"
              className="input"
              value={cdName}
              onChange={(e) => setCdName(e.target.value)}
              placeholder="Ex: CD Osasco / Distribuição"
            />
          </div>
          <div className="field" style={{ position: 'relative' }}>
            <label>Endereço completo</label>
            <input
              type="text"
              className="input"
              value={cdAddress}
              onChange={(e) => handleCdAddressSearch(e.target.value)}
              onKeyDown={(event) => handleSuggestionKeyDown(event, cdSuggestions, showCdSuggest, setShowCdSuggest, activeCdSuggestion, setActiveCdSuggestion, selectCdAddress)}
              placeholder="Ex: Av. dos Autonomistas, 3000"
              role="combobox"
              aria-expanded={showCdSuggest}
              aria-controls="fleet-cd-suggestions"
              aria-activedescendant={activeCdSuggestion >= 0 ? `fleet-cd-option-${activeCdSuggestion}` : undefined}
            />
            {showCdSuggest && cdSuggestions.length > 0 && (
              <div id="fleet-cd-suggestions" className="addr-suggest" style={{ zIndex: 10 }} role="listbox">
                {cdSuggestions.map((a, idx) => (
                  <div id={`fleet-cd-option-${idx}`} key={idx} className={`addr-suggest-item ${activeCdSuggestion === idx ? 'keyboard-active' : ''}`} role="option" aria-selected={activeCdSuggestion === idx} onClick={() => selectCdAddress(a)}>
                    <div>
                      <div className="a">{a.label}</div>
                      <div className="c">{a.city} / {a.uf}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="field-row">
            <div className="field">
              <label>Cidade</label>
              <input type="text" className="input" value={cdCity} onChange={(e) => setCdCity(e.target.value)} />
            </div>
            <div className="field">
              <label>UF</label>
              <input type="text" className="input" maxLength={2} value={cdUf} onChange={(e) => setCdUf(e.target.value.toUpperCase())} />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Latitude</label>
              <input type="number" step="any" className="input" value={cdLat} onChange={(e) => setCdLat(e.target.value)} />
            </div>
            <div className="field">
              <label>Longitude</label>
              <input type="number" step="any" className="input" value={cdLng} onChange={(e) => setCdLng(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label>Tipo de Operação</label>
            <select className="input" value={cdType} onChange={(e) => setCdType(e.target.value as any)}>
              <option value="matriz">Matriz</option>
              <option value="distribuicao">Distribuição</option>
              <option value="crossdock">Cross-docking</option>
            </select>
          </div>
        </div>
      </Modal>

      {/* Loading Overlay */}
      {optimizing && (
        <div className="opt-overlay">
          <div className="opt-spinner"></div>
          <div className="opt-msg">Resolvendo roteirização com OR-Tools...</div>
          <div className="opt-detail">Calculando matriz de distâncias e balanceando carga por veículo</div>
        </div>
      )}
    </Layout>
  );
}
